import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import supabase from '../supabaseClient';

const ESTADOS = ["pendiente", "aceptado", "en_proceso", "terminado", "cancelado"];
const ESTADO_LABEL = { pendiente: "Pendiente", aceptado: "Aceptado", en_proceso: "En proceso", terminado: "Terminado", cancelado: "Cancelado" };
const ESTADO_COLOR = { pendiente: "#9ca3af", aceptado: "#3b82f6", en_proceso: "#f59e0b", terminado: "#22c55e", cancelado: "#ef4444" };
const PERIODOS = [3, 6, 12];

const claveMes = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const labelMes = (clave) => { const [y, m] = clave.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("es-ES", { month: "short", year: "2-digit" }); };
const mesesRango = (n) => {
  const arr = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return arr;
};
const inicioRango = (n) => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - (n - 1), 1).toISOString();
};

// ── OPERARIO (INTERNACIONAL): PANEL KPIs ──────────────────────────
export const KPIsInternacionalSection = ({ currentUser }) => {
  const navigate = useNavigate();
  const almacen_id = currentUser?.almacen_id;

  const [stock, setStock] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [cargandoBase, setCargandoBase] = useState(true);

  const [periodo, setPeriodo] = useState(6);
  const [movimientos, setMovimientos] = useState([]);
  const [registrosSalida, setRegistrosSalida] = useState([]);
  const [cargandoPeriodo, setCargandoPeriodo] = useState(true);

  useEffect(() => {
    if (!almacen_id) return;
    setCargandoBase(true);
    Promise.all([
      supabase.from('stock').select('*').eq('almacen_id', almacen_id),
      supabase.from('pedidos').select('*').eq('almacen_id', almacen_id),
    ]).then(([s, p]) => {
      setStock(s.data || []);
      setPedidos(p.data || []);
    }).finally(() => setCargandoBase(false));
  }, [almacen_id]);

  useEffect(() => {
    if (!almacen_id) return;
    setCargandoPeriodo(true);
    const desde = inicioRango(periodo);
    Promise.all([
      supabase.from('movimientos_stock').select('tipo,cantidad_cajas,creado_en').eq('almacen_id', almacen_id).in('tipo', ['entrada', 'salida']).gte('creado_en', desde),
      supabase.from('registros').select('created_at').eq('almacen_id', almacen_id).eq('tipo', 'Salida Contenedor').gte('created_at', desde),
    ]).then(([m, r]) => {
      setMovimientos(m.data || []);
      setRegistrosSalida(r.data || []);
    }).finally(() => setCargandoPeriodo(false));
  }, [almacen_id, periodo]);

  const pedidosEnCurso = useMemo(() => pedidos.filter(p => ["pendiente", "aceptado", "en_proceso"].includes(p.estado)).length, [pedidos]);
  const mesActual = claveMes(new Date().toISOString());
  const pedidosTerminadosMes = useMemo(() => pedidos.filter(p => p.estado === "terminado" && claveMes(p.terminado_en || p.creado_en) === mesActual).length, [pedidos, mesActual]);
  const contenedoresMes = useMemo(() => registrosSalida.filter(r => claveMes(r.created_at) === mesActual).length, [registrosSalida, mesActual]);

  const referenciasDistintas = useMemo(() => new Set(stock.map(s => s.referencia)).size, [stock]);
  const totalCajas = useMemo(() => stock.reduce((s, r) => s + (r.cantidad_cajas || 0), 0), [stock]);
  const totalPalets = useMemo(() => stock.reduce((s, r) => s + (r.cantidad_palets || 0), 0), [stock]);

  const { vencidos, proximos } = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const en30 = new Date(hoy); en30.setDate(en30.getDate() + 30);
    let v = 0, p = 0;
    for (const s of stock) {
      if (!s.fecha_caducidad) continue;
      const f = new Date(`${s.fecha_caducidad}T00:00:00`);
      if (f < hoy) v++;
      else if (f <= en30) p++;
    }
    return { vencidos: v, proximos: p };
  }, [stock]);

  const entradasSalidasPorMes = useMemo(() => mesesRango(periodo).map(clave => ({
    mes: labelMes(clave),
    entradas: movimientos.filter(m => m.tipo === "entrada" && claveMes(m.creado_en) === clave).reduce((s, m) => s + (m.cantidad_cajas || 0), 0),
    salidas: movimientos.filter(m => m.tipo === "salida" && claveMes(m.creado_en) === clave).reduce((s, m) => s + (m.cantidad_cajas || 0), 0),
  })), [movimientos, periodo]);

  const contenedoresPorMes = useMemo(() => mesesRango(periodo).map(clave => ({
    mes: labelMes(clave),
    contenedores: registrosSalida.filter(r => claveMes(r.created_at) === clave).length,
  })), [registrosSalida, periodo]);

  const pedidosPorEstado = useMemo(() => ESTADOS.map(e => ({
    estado: ESTADO_LABEL[e], value: pedidos.filter(p => p.estado === e).length, color: ESTADO_COLOR[e],
  })), [pedidos]);

  const hayMovimientosPeriodo = entradasSalidasPorMes.some(m => m.entradas > 0 || m.salidas > 0);
  const hayContenedoresPeriodo = contenedoresPorMes.some(c => c.contenedores > 0);
  const hayPedidos = pedidos.length > 0;

  const alertaCaducidad = vencidos > 0 || proximos > 0;
  const alertaClase = vencidos > 0 ? "bg-red-50 border-red-300 text-red-700" : proximos > 0 ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-gray-100 text-gray-800";

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="font-bold text-gray-800">📊 Panel KPIs</h2>
        <p className="text-xs text-gray-500">Indicadores del almacén Internacional</p>
      </div>

      {cargandoBase ? (
        <p className="text-sm text-gray-400 text-center py-4">Cargando...</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase font-semibold">Pedidos en curso</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{pedidosEnCurso}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase font-semibold">Terminados este mes</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{pedidosTerminadosMes}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase font-semibold">Contenedores este mes</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{contenedoresMes}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase font-semibold">Referencias en stock</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{referenciasDistintas}</p>
            <p className="text-xs text-gray-400 mt-0.5">{totalCajas} cajas · {totalPalets} palets</p>
          </div>
          <div
            onClick={() => navigate("/app/home/inventario-stock")}
            className={`rounded-xl shadow p-4 border cursor-pointer hover:opacity-90 transition-all ${alertaClase}`}
          >
            <p className="text-xs uppercase font-semibold opacity-80">Alertas caducidad</p>
            <p className="text-2xl font-bold mt-1">{vencidos + proximos}</p>
            <p className="text-xs opacity-70 mt-0.5">{vencidos} vencidos · {proximos} &lt;30 días</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs font-semibold text-gray-500 uppercase">Periodo de las gráficas</p>
          <div className="flex gap-2">
            {PERIODOS.map(p => (
              <button
                key={p}
                onClick={() => setPeriodo(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${periodo === p ? "bg-violet-700 text-white border-violet-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
              >
                {p} meses
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 space-y-2">
        <p className="text-sm font-bold text-gray-800">Entradas vs salidas por mes (cajas)</p>
        {cargandoPeriodo ? (
          <p className="text-sm text-gray-400 text-center py-8">Cargando...</p>
        ) : !hayMovimientosPeriodo ? (
          <p className="text-sm text-gray-500 text-center py-8">Sin datos de este periodo.</p>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={entradasSalidasPorMes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="entradas" name="Entradas" fill="#0891b2" radius={[4, 4, 0, 0]} />
              <Bar dataKey="salidas" name="Salidas" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-4 space-y-2">
        <p className="text-sm font-bold text-gray-800">Pedidos por estado</p>
        {cargandoBase ? (
          <p className="text-sm text-gray-400 text-center py-8">Cargando...</p>
        ) : !hayPedidos ? (
          <p className="text-sm text-gray-500 text-center py-8">Sin pedidos todavía.</p>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pedidosPorEstado} dataKey="value" nameKey="estado" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {pedidosPorEstado.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-4 space-y-2">
        <p className="text-sm font-bold text-gray-800">Contenedores cargados por mes</p>
        {cargandoPeriodo ? (
          <p className="text-sm text-gray-400 text-center py-8">Cargando...</p>
        ) : !hayContenedoresPeriodo ? (
          <p className="text-sm text-gray-500 text-center py-8">Sin datos de este periodo.</p>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={contenedoresPorMes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="contenedores" name="Contenedores" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {!alertaCaducidad && (
        <p className="text-xs text-gray-400 text-center">✅ Sin alertas de caducidad activas</p>
      )}
    </div>
  );
};
